<template>
  <UiLangSelect
    v-if="options.length > 1"
    :modelValue="currentLocale"
    :options="options"
    :ariaLabel="$t('W: Vælg sprog')"
    @update:model-value="onLanguageChange" />
</template>

<script lang="ts">
import { i18n } from '@/locales'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { useUserStore } from '@/stores/userStore'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useToast } from '@/globals/typescript/utils/toast'
import type { ILangSelectOption } from '@team-milva/lab-components'
import {
  LOCALE_COUNTRY_CODES,
  LOCALE_NATIVE_NAMES,
  type TLabManagementLanguageCode,
  type TLabManagementLocale,
} from '@team-milva/lab-models/language'
import { mapState } from 'pinia'

export default {
  name: 'LangSelect',
  computed: {
    ...mapState(useAccountStore, ['availableLanguages']),
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useUserStore, ['currentUser', 'isLabAdminUser']),
    options(): ILangSelectOption[] {
      return this.availableLanguages.map((lang) => ({
        value: lang.locale,
        code: lang.code,
        text: LOCALE_NATIVE_NAMES[lang.locale as TLabManagementLocale] ?? lang.name,
        countryCode: LOCALE_COUNTRY_CODES[lang.locale as TLabManagementLocale] ?? lang.locale,
      }))
    },
  },
  methods: {
    async onLanguageChange(locale: string) {
      i18n.global.locale.value = locale as TLabManagementLocale
      localStorage.setItem('lang', locale)

      // Admins keep their own independent, already-persisted language
      // (edited from their lab-admin profile) — this switcher only persists
      // for a genuine LabManagementUser session.
      if (!this.currentUser || this.isLabAdminUser) return

      const languageCode = this.availableLanguages.find(
        (lang) => lang.locale === locale
      )?.code
      if (!languageCode) return

      try {
        const result = await managementApi('patch', '/users/{userID}', {
          path: { userID: this.currentUser.id },
          body: { languageCode: languageCode as TLabManagementLanguageCode },
        })
        if (!result.success) throw new Error(result.code)
        useToast().success(this.$t('W: Sprog gemt'))
      } catch {
        useToast().error(this.$t('W: Kunne ikke gemme sprog'))
      }
    },
  },
}
</script>
